/**
 * 场景序列化器
 *
 * 负责整个场景的序列化和反序列化，包括实体、组件等
 */
import { GlobalComponentRegistry } from '../Core/ComponentStorage';
import { EntitySerializer } from './EntitySerializer';
import { getComponentTypeName } from '../Decorators';
import { getSerializationMetadata } from './SerializationDecorators';
import { BinarySerializer } from '../../Utils/BinarySerializer';
import { HierarchySystem } from '../Systems/HierarchySystem';
import { HierarchyComponent } from '../Components/HierarchyComponent';
import { SerializationContext } from './SerializationContext';
import { ValueSerializer } from './ValueSerializer';
import { createLogger } from '../../Utils/Logger';
const logger = createLogger('SceneSerializer');
/**
 * 场景序列化器类
 */
export class SceneSerializer {
    /**
     * 序列化场景
     *
     * @param scene 要序列化的场景
     * @param options 序列化选项
     * @returns 序列化后的数据（JSON字符串或二进制Uint8Array）
     */
    static serialize(scene, options) {
        const opts = {
            systems: false,
            format: 'json',
            pretty: true,
            includeMetadata: true,
            ...options
        };
        // 过滤实体和组件
        const entities = this.filterEntities(scene, opts);
        // 获取层级系统用于序列化子实体
        const hierarchySystem = scene.getSystem(HierarchySystem);
        // 序列化实体（传入 hierarchySystem 以正确序列化子实体）
        const serializedEntities = EntitySerializer.serializeEntities(entities, true, hierarchySystem ?? undefined);
        // 收集组件类型信息
        const componentTypeRegistry = this.buildComponentTypeRegistry(entities);
        // 序列化场景自定义数据
        const sceneData = this.serializeSceneData(scene.sceneData);
        // 构建序列化数据
        const serializedScene = {
            name: scene.name,
            version: this.SERIALIZATION_VERSION,
            entities: serializedEntities,
            componentTypeRegistry
        };
        // 添加场景数据（如果有）
        if (sceneData && Object.keys(sceneData).length > 0) {
            serializedScene.sceneData = sceneData;
        }
        // 添加元数据
        if (opts.includeMetadata) {
            serializedScene.timestamp = Date.now();
            serializedScene.metadata = {
                entityCount: serializedEntities.length,
                componentTypeCount: componentTypeRegistry.length,
                serializationOptions: opts
            };
        }
        if (opts.format === 'json') {
            return opts.pretty
                ? JSON.stringify(serializedScene, null, 2)
                : JSON.stringify(serializedScene);
        }
        else {
            return BinarySerializer.encode(serializedScene);
        }
    }
    /**
     * 反序列化场景
     *
     * 使用两阶段反序列化：
     * 1. 创建所有实体和组件，收集待解析的 EntityRef
     * 2. 解析所有 EntityRef，建立正确的对象引用
     *
     * Deserialize scene using two-phase approach:
     * 1. Create all entities and components, collect pending EntityRefs
     * 2. Resolve all EntityRefs, establish correct object references
     *
     * @param scene 目标场景
     * @param saveData 序列化的数据（JSON字符串或二进制Uint8Array）
     * @param options 反序列化选项
     */
    static deserialize(scene, saveData, options) {
        const opts = {
            strategy: 'replace',
            preserveIds: false,
            ...options
        };
        let serializedScene;
        try {
            if (typeof saveData === 'string') {
                serializedScene = JSON.parse(saveData);
            }
            else {
                serializedScene = BinarySerializer.decode(saveData);
            }
        }
        catch (error) {
            throw new Error(`Failed to parse save data: ${error}`);
        }
        // 版本迁移
        if (opts.migration && serializedScene.version !== this.SERIALIZATION_VERSION) {
            serializedScene = opts.migration(serializedScene.version, this.SERIALIZATION_VERSION, serializedScene);
        }
        // 构建组件注册表
        const componentRegistry = opts.componentRegistry || this.getGlobalComponentRegistry();
        // 根据策略处理场景
        if (opts.strategy === 'replace') {
            // 清空场景
            scene.destroyAllEntities();
        }
        // ID生成器
        const idGenerator = () => scene.identifierPool.checkOut();
        // 获取层级系统
        const hierarchySystem = scene.getSystem(HierarchySystem);
        // ========== 阶段 1：创建实体和组件，收集 EntityRef ==========
        // Phase 1: Create entities and components, collect EntityRefs
        const context = new SerializationContext();
        context.setPreserveIds(opts.preserveIds || false);
        // 反序列化实体（传入 context 收集 EntityRef）
        const { rootEntities, allEntities } = EntitySerializer.deserializeEntities(serializedScene.entities, componentRegistry, idGenerator, opts.preserveIds || false, scene, hierarchySystem, context);
        // 将所有实体添加到场景（包括子实体）
        // 先添加根实体，再递归添加子实体
        for (const entity of rootEntities) {
            scene.addEntity(entity, true);
            this.addChildrenRecursively(entity, scene, hierarchySystem, allEntities);
        }
        // 统一清理缓存（批量操作完成后）
        scene.querySystem.clearCache();
        scene.clearSystemEntityCaches();
        // ========== 阶段 2：解析所有 EntityRef ==========
        // Phase 2: Resolve all EntityRefs
        const resolvedCount = context.resolveAllReferences();
        const unresolvedCount = context.getUnresolvedCount();
        if (unresolvedCount > 0) {
            logger.warn(`${unresolvedCount} EntityRef(s) could not be resolved. ` +
                `Resolved: ${resolvedCount}, Total pending: ${context.getPendingCount()} | ` +
                `${unresolvedCount} 个实体引用无法解析`);
        }
        // 反序列化场景自定义数据
        if (serializedScene.sceneData) {
            this.deserializeSceneData(serializedScene.sceneData, scene.sceneData);
        }
        // 调用所有组件的 onDeserialized 生命周期方法
        // Call onDeserialized lifecycle method on all components
        const deserializedPromises = [];
        for (const entity of allEntities.values()) {
            this.callOnDeserializedForEntity(entity, deserializedPromises);
        }
        // 如果有异步的 onDeserialized，在后台执行
        if (deserializedPromises.length > 0) {
            Promise.all(deserializedPromises).catch(error => {
                logger.error('Error in onDeserialized | onDeserialized 执行错误:', error);
            });
        }
    }
    /**
     * 调用实体所有组件的 onDeserialized 方法（不递归）
     */
    static callOnDeserializedForEntity(entity, promises) {
        for (const component of entity.components) {
            try {
                const result = component.onDeserialized();
                if (result instanceof Promise) {
                    promises.push(result);
                }
            }
            catch (error) {
                const typeName = getComponentTypeName(component.constructor);
                logger.error(`Error calling onDeserialized on component ${typeName} | 调用组件 ${typeName} 的 onDeserialized 时出错:`, error);
            }
        }
    }
    /**
     * 递归添加实体的所有子实体到场景
     *
     * 修复反序列化时子实体丢失的问题：
     * EntitySerializer.deserialize会提前设置子实体的scene引用，
     * 导致Entity.addChild的条件判断(!child.scene)跳过scene.addEntity调用。
     * 因此需要在SceneSerializer中统一递归添加所有子实体。
     *
     * @param entity 父实体
     * @param scene 目标场景
     * @param hierarchySystem 层级系统
     */
    static addChildrenRecursively(entity, scene, hierarchySystem, childEntitiesMap) {
        const hierarchy = entity.getComponent(HierarchyComponent);
        if (!hierarchy || hierarchy.childIds.length === 0)
            return;
        // 获取子实体
        // 注意：此时子实体还没有被添加到场景，所以不能用 scene.findEntityById
        // 需要从 childEntitiesMap 中查找（如果提供了的话）
        for (const childId of hierarchy.childIds) {
            // 尝试从 map 中获取，否则从场景获取（用于已添加的情况）
            const child = childEntitiesMap?.get(childId) ?? scene.findEntityById(childId);
            if (child) {
                scene.addEntity(child, true); // 延迟缓存清理
                this.addChildrenRecursively(child, scene, hierarchySystem, childEntitiesMap);
            }
        }
    }
    static serializeSceneData(sceneData) {
        const result = {};
        for (const [key, value] of sceneData) {
            result[key] = ValueSerializer.serialize(value);
        }
        return result;
    }
    static deserializeSceneData(data, targetMap) {
        targetMap.clear();
        for (const [key, value] of Object.entries(data)) {
            targetMap.set(key, ValueSerializer.deserialize(value));
        }
    }
    /**
     * 过滤要序列化的实体和组件
     */
    static filterEntities(scene, options) {
        const entities = Array.from(scene.entities.buffer);
        // 如果指定了组件类型过滤
        if (options.components && options.components.length > 0) {
            const componentTypeSet = new Set(options.components);
            // 只返回拥有指定组件的实体
            return entities.filter((entity) => {
                return Array.from(entity.components).some((component) => componentTypeSet.has(component.constructor));
            });
        }
        return entities;
    }
    /**
     * 构建组件类型注册表
     */
    static buildComponentTypeRegistry(entities) {
        const registry = new Map();
        for (const entity of entities) {
            for (const component of entity.components) {
                const componentType = component.constructor;
                const typeName = getComponentTypeName(componentType);
                const metadata = getSerializationMetadata(component);
                if (metadata && !registry.has(typeName)) {
                    registry.set(typeName, metadata.options.version);
                }
            }
        }
        return Array.from(registry.entries()).map(([typeName, version]) => ({
            typeName,
            version
        }));
    }
    /**
     * 获取全局组件注册表
     *
     * 从所有已注册的组件类型构建注册表
     */
    static getGlobalComponentRegistry() {
        return GlobalComponentRegistry.getAllComponentNames();
    }
    /**
     * 验证保存数据的有效性
     *
     * @param saveData 序列化的数据
     * @returns 验证结果
     */
    static validate(saveData) {
        const errors = [];
        try {
            const data = JSON.parse(saveData);
            if (!data.version) {
                errors.push('Missing version field');
            }
            if (!data.entities || !Array.isArray(data.entities)) {
                errors.push('Missing or invalid entities field');
            }
            if (!data.componentTypeRegistry || !Array.isArray(data.componentTypeRegistry)) {
                errors.push('Missing or invalid componentTypeRegistry field');
            }
            return {
                valid: errors.length === 0,
                version: data.version,
                ...(errors.length > 0 && { errors })
            };
        }
        catch (error) {
            return {
                valid: false,
                errors: [`JSON parse error: ${error}`]
            };
        }
    }
    /**
     * 获取保存数据的信息（不完全反序列化）
     *
     * @param saveData 序列化的数据
     * @returns 保存数据的元信息
     */
    static getInfo(saveData) {
        try {
            const data = JSON.parse(saveData);
            return {
                name: data.name,
                version: data.version,
                ...(data.timestamp !== undefined && { timestamp: data.timestamp }),
                entityCount: data.metadata?.entityCount || data.entities.length,
                componentTypeCount: data.componentTypeRegistry.length
            };
        }
        catch (error) {
            return null;
        }
    }
}
/**
 * 当前序列化版本
 */
SceneSerializer.SERIALIZATION_VERSION = 1;
//# sourceMappingURL=SceneSerializer.js.map